import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { getLeaderboardPage } from '../store/analytics/thunks';
import { getUserGroups } from '../store/social/thunks';
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import LeaderboardSkeleton from './LeaderboardSkeleton';
import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
import usePagination from "../hooks/usePagination";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Center,
  IconButton,
  useColorModeValue,
  Button,
  Select,
  Box,
  Avatar,
  Badge,
  HStack,
  VStack,
} from "@chakra-ui/react";


function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Leaderboard({ user }) {
  const navigate = useNavigate();
  const showToast = useToastNotification();
  const dispatch = useDispatch();
  const query = useQuery();
  const { leaderboard } = useSelector((state) => state.analytics);
  const { groups } = useSelector((state) => state.social);
  const hasLeaderboard = ((leaderboard && leaderboard.leaderboard && Object.keys(leaderboard.leaderboard).length > 0) ? (true):(false));
  const hasGroups = ((groups && groups.groups && groups.groups.length > 0) ? (true):(false));
  const { preferences } = useSelector((state) => state.user);
  const hasPreferences = ((preferences && Object.keys(preferences).length > 0) ? (true):(false));

  // Same pattern as Home/PnlCalendar/Summary: state.ui.loading flips false on
  // first concurrent thunk resolve. Empty-state shouldn't render until both
  // leaderboard + groups slices have been populated.
  const leaderboardLoaded = leaderboard && Object.keys(leaderboard).length > 0;
  const groupsLoaded = groups && Object.keys(groups).length > 0;
  const initialDataLoaded = leaderboardLoaded && groupsLoaded;
  const { loading: authLoading } = useAsyncState({
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });

  // Color mode values
  const bgColor = useColorModeValue("gray.200", "gray.800");
  const cardBg = useColorModeValue("whiteAlpha.900", "gray.800");
  const activeBg = useColorModeValue("blue.500", "blue.200");
  const activeColor = useColorModeValue("white", "gray.800");
  const inactiveBg = useColorModeValue("gray.100", "gray.600");
  const inactiveColor = useColorModeValue("gray.700", "gray.200");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("white", "gray.700");
  const userRowBg = useColorModeValue("blue.50", "blue.900");

  const [leaderboardTimeFilter, setLeaderboardTimeFilter] = useState("All Time");
  const [leaderboardStatFilter, setLeaderboardStatFilter] = useState("Total PNL");
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [profileStatusPopUpEnable, setProfileStatusPopUpEnable] = useState(true);
  const profileToastTriggered = React.useRef(false);

  const {
    page,
    totalCount,
    numResults: num_results,
    setNumResults,
    startOffset: pageStartOffset,
    endOffset: pageEndOffset,
    canGoNext: nextPageEnable,
    canGoPrev: backPageEnable,
  } = usePagination(leaderboard, { defaultPageSize: 10 });

  useEffect(() => {
    if (profileStatusPopUpEnable && hasPreferences) {
      profileStatusToast();
      setProfileStatusPopUpEnable(false);
    }
  }, [profileStatusPopUpEnable, preferences]);

  useEffect(() => {
    handleLeaderboardFilterChange();
  }, [leaderboardTimeFilter,leaderboardStatFilter,selectedGroupId]);

  useEffect(() => {
    dispatch(getUserGroups({}));
    const savedLeaderboardFilters = window.localStorage.getItem('LeaderboardFilters');
    if (savedLeaderboardFilters) {
      const LeaderboardFilters = JSON.parse(savedLeaderboardFilters);
      setLeaderboardStatFilter(LeaderboardFilters.value_filter || "Total PNL");
      setLeaderboardTimeFilter(LeaderboardFilters.time_filter || "All Time");
      setSelectedGroupId(LeaderboardFilters.group_id || null);
    }
  }, []);
  const handleFilterChange = (newFilter) => {
    query.set('filter', newFilter);
    navigate(`?${query.toString()}`);
  };

  function filtersToQueryString(filters) {
    const params = new URLSearchParams();
    for (const key in filters) {
      if (filters[key] !== '') {
        params.append(key, filters[key]);
      }
    }
    return params.toString();
  }

  const handleLeaderboardFilterChange = async () => {
    const encodedTimeFilter = encodeURIComponent(leaderboardTimeFilter);
    const encodedValueFilter = encodeURIComponent(leaderboardStatFilter);
    const filters = {};
    filters.page = 1;
    filters.numrows = num_results;
    filters.time_filter = encodedTimeFilter;
    filters.value_filter = encodedValueFilter;
    if (selectedGroupId) {
      filters.group_id = selectedGroupId;
    }
    await dispatch(getLeaderboardPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    const saveFilters = {};
    saveFilters.time_filter = leaderboardTimeFilter;
    saveFilters.value_filter = leaderboardStatFilter;
    if (selectedGroupId) {
      saveFilters.group_id = selectedGroupId;
    }
    window.localStorage.setItem('LeaderboardFilters', JSON.stringify(saveFilters));
  }

  const profileStatusToast = () => {
    if (profileToastTriggered.current) return;

    const message = `Your Profile is ${
      hasPreferences && preferences["public_profile_optin"] === 1 ? "Public" : "Private"
    }`;

    showToast(message, {

      description: "You can change your leaderboard visibility in user settings",

      variant: "solid",

      status: "info",

      duration: 5000,

      position: "top",
    });

    profileToastTriggered.current = true;
  };

  const handleNextPage = async (e) => {
    if(nextPageEnable){
      const encodedTimeFilter = encodeURIComponent(leaderboardTimeFilter);
      const encodedValueFilter = encodeURIComponent(leaderboardStatFilter);
      const filters = {};
      filters.page = page+1;
      filters.numrows = num_results;
      filters.time_filter = encodedTimeFilter;
      filters.value_filter = encodedValueFilter;
      if (selectedGroupId) {
        filters.group_id = selectedGroupId;
      }
      await dispatch(getLeaderboardPage({ filters }));
      handleFilterChange(filtersToQueryString(filters));
      const saveFilters = {};
      saveFilters.time_filter = leaderboardTimeFilter;
      saveFilters.value_filter = leaderboardStatFilter;
      if (selectedGroupId) {
        saveFilters.group_id = selectedGroupId;
      }
      window.localStorage.setItem('LeaderboardFilters', JSON.stringify(saveFilters));
    }
  }

  const handleBackPage = async (e) => {
    if(backPageEnable){
      const encodedTimeFilter = encodeURIComponent(leaderboardTimeFilter);
      const encodedValueFilter = encodeURIComponent(leaderboardStatFilter);
      const filters = {};
      filters.page = page-1;
      filters.numrows = num_results;
      filters.time_filter = encodedTimeFilter;
      filters.value_filter = encodedValueFilter;
      if (selectedGroupId) {
        filters.group_id = selectedGroupId;
      }
      await dispatch(getLeaderboardPage({ filters }));
      handleFilterChange(filtersToQueryString(filters));
      const saveFilters = {};
      saveFilters.time_filter = leaderboardTimeFilter;
      saveFilters.value_filter = leaderboardStatFilter;
      if (selectedGroupId) {
        saveFilters.group_id = selectedGroupId;
      }
      window.localStorage.setItem('LeaderboardFilters', JSON.stringify(saveFilters));
    }
  }

  const handleChangeNumResults = async (e) => {
    const new_num_results = e.target.value;
    setNumResults(new_num_results);
    const encodedTimeFilter = encodeURIComponent(leaderboardTimeFilter);
    const encodedValueFilter = encodeURIComponent(leaderboardStatFilter);
    const filters = {};
    filters.page = 1;
    filters.numrows = new_num_results;
    filters.time_filter = encodedTimeFilter;
    filters.value_filter = encodedValueFilter;
    if (selectedGroupId) {
      filters.group_id = selectedGroupId;
    }
    await dispatch(getLeaderboardPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    const saveFilters = {};
    saveFilters.time_filter = leaderboardTimeFilter;
    saveFilters.value_filter = leaderboardStatFilter;
    if (selectedGroupId) {
      saveFilters.group_id = selectedGroupId;
    }
    window.localStorage.setItem('LeaderboardFilters', JSON.stringify(saveFilters));
  }

  return (
      <Flex
        flexDirection="column"
        height="100vh"
        backgroundColor={bgColor}
      >
        <Box flex="auto" display="flex" borderWidth="1px" rounded="lg" overflow="hidden" overflowX="auto" overflowY="auto" m={2}>
          {(authLoading || !initialDataLoaded) ? (
            <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={cardBg} boxShadow="md">
              <LeaderboardSkeleton />
            </VStack>
          ) : (
            <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={cardBg} boxShadow="md" overflowX="auto" overflowY="auto">
              {/* Stat Filters */}
              <HStack spacing={2} flexWrap="wrap" alignItems="center">
                <Text fontSize="xs" fontWeight="bold" color={textColor} flexShrink={0}>Metric:</Text>
                {["Total PNL", "Avg PNL", "Win %", "Largest Win", "Avg Win", "Avg Loss"].map((filter) => (
                  <Button
                    key={filter}
                    size="sm"
                    px={4}
                    bg={leaderboardStatFilter === filter ? activeBg : inactiveBg}
                    color={leaderboardStatFilter === filter ? activeColor : inactiveColor}
                    _hover={{ opacity: 0.85 }}
                    onClick={() => setLeaderboardStatFilter(filter)}
                    borderRadius="full"
                  >
                    {filter}
                  </Button>
                ))}
              </HStack>

              {/* Time Filters */}
              <HStack spacing={2} flexWrap="wrap" alignItems="center">
                <Text fontSize="xs" fontWeight="bold" color={textColor} flexShrink={0}>Period:</Text>
                {["All Time", "YTD", "Quarter", "Month", "Week", "Today"].map((filter) => (
                  <Button
                    key={filter}
                    size="sm"
                    px={4}
                    bg={leaderboardTimeFilter === filter ? activeBg : inactiveBg}
                    color={leaderboardTimeFilter === filter ? activeColor : inactiveColor}
                    _hover={{ opacity: 0.85 }}
                    onClick={() => setLeaderboardTimeFilter(filter)}
                    borderRadius="full"
                  >
                    {filter}
                  </Button>
                ))}
              </HStack>

              {/* Group Filter */}
              {hasGroups && (
                <HStack spacing={2} alignItems="center">
                  <Text fontSize="sm" fontWeight="bold" color={textColor}>Group:</Text>
                  <Select
                    placeholder="All Users"
                    value={selectedGroupId || ""}
                    onChange={(e) => setSelectedGroupId(e.target.value ? parseInt(e.target.value) : null)}
                    maxW="140px"
                    minW="110px"
                    size="sm"
                    borderRadius="md"
                  >
                    {groups.groups.map((group) => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.name}
                      </option>
                    ))}
                  </Select>
                </HStack>
              )}

              {/* Table + Pagination */}
              {hasLeaderboard ? (
                <Box maxW="700px" mx="auto" w="full">
                  <TableContainer overflowY="auto" overflowX="auto" rounded="lg" borderWidth="1px" borderColor={borderColor}>
                    <Table size="sm" variant="simple" colorScheme="gray">
                      <Thead position="sticky" top={0} bgColor={headerBg} zIndex={2}>
                        <Tr>
                          <Th width="60px">Position</Th>
                          <Th>User</Th>
                          <Th isNumeric>Value <Badge colorScheme="blue" variant="subtle" fontSize="xx-small" borderRadius="full" ml={1} fontWeight="medium">{leaderboardStatFilter}</Badge></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {leaderboard.leaderboard.map((entry, index) => (
                          <Tr key={index} bg={entry.current_user ? userRowBg : "transparent"}>
                            <Td>{index + 1}</Td>
                            <Td>
                              <Box display="flex" alignItems="center">
                                <Avatar
                                  zIndex={0}
                                  size="xs"
                                  src={entry.profile_picture_url ? entry.profile_picture_url : null}
                                  mr={2}
                                />
                                {entry.display_name}
                                {entry.current_user && (
                                  <Badge size="sm" colorScheme="blue" variant="subtle" borderRadius="full" ml={2}>You</Badge>
                                )}
                              </Box>
                            </Td>
                            <Td isNumeric>{entry.leaderboard_value}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  {/* Pagination — below table, aligned to its right edge */}
                  <HStack spacing={1} pt={2} justifyContent="flex-end" flexWrap="wrap">
                    <HStack spacing={1}>
                      <Text fontSize="xs" fontWeight="semibold">Rows:</Text>
                      <Select maxW={70} variant="filled" size="xs" value={num_results} onChange={(e) => handleChangeNumResults(e)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="250">250</option>
                      </Select>
                    </HStack>
                    <HStack spacing={0}>
                      <IconButton
                        aria-label="Previous page"
                        icon={<ChevronLeftIcon />}
                        size="xs"
                        minW="20px"
                        h="20px"
                        variant="ghost"
                        borderRadius="full"
                        isDisabled={!backPageEnable}
                        onClick={e => handleBackPage(e)}
                      />
                      <IconButton
                        aria-label="Next page"
                        icon={<ChevronRightIcon />}
                        size="xs"
                        minW="20px"
                        h="20px"
                        variant="ghost"
                        borderRadius="full"
                        isDisabled={!nextPageEnable}
                        onClick={e => handleNextPage(e)}
                      />
                      <Text fontSize="xs" fontWeight="semibold">
                        {pageStartOffset}-{pageEndOffset} of {totalCount}
                      </Text>
                    </HStack>
                  </HStack>
                </Box>
              ) : (
                <Center py={10}>
                  <Text color={textColor} fontSize="sm">No leaderboard data available</Text>
                </Center>
              )}
            </VStack>
          )}
        </Box>
      </Flex>
  )
}
